import { occurrenceChatId } from "@edu/shared";
import type { ScheduleEntry } from "@edu/shared";
import { GROUP_FORM_NOTE, chatBody, type ChatBody } from "../occurrence-chat/chat-text.ts";
import type { OccurrenceChatState } from "../occurrence-chat/use-occurrence-chat.ts";

/**
 * Чат занятия в КАРТОЧКЕ: адрес, слова и решение о том, что показывать.
 *
 * Проект целиком в `docs/room-chat-design.md`, это его разделы 12 и 13. Мест
 * показа у карточки два (всплывающая карточка в сетке и строка списка уроков),
 * и оба берут решения отсюда: тестов на компоненты в проекте нет и не
 * планируется (раздел 9 CLAUDE.md), а ломается здесь каждое решение молча.
 *
 * ⚠️ **Слова карточки не те же, что в комнате, и это не разнобой.** В комнате
 * человек сидит на занятии, поэтому там говорится «до занятия и после него»; в
 * карточку он пришёл из расписания, и ему надо сказать, КОМУ он пишет. Плюс у
 * ученика и у преподавателя собеседник разный, поэтому текстов четыре, а не два
 * (раздел 13 проекта перечисляет их дословно).
 */

/**
 * Заголовок блока.
 *
 * ⚠️ **«Чат», а не «Чат занятия»** (раздел 13 проекта): слово «занятия» внутри
 * карточки занятия указывает на то, что и так рядом. В комнате имя полное, там
 * карточки вокруг нет.
 */
export const ENTRY_CHAT_TITLE = "Чат";

/**
 * Адрес чата этого занятия, либо `null`, если чата у него быть не может.
 *
 * ⚠️ **Порядок веток НЕ переставляется, и это не стиль.** Группа идёт первой:
 * у сводной строки занятия группы заполнен и `slotId` (представитель пучка), но
 * общий чат лежит под ключом ГРУППЫ, и счётчик непрочитанного отвечает
 * преподавателю ровно этим адресом. Взяв слот, мы получили бы адрес, который
 * сервер всё равно разрешит в тот же чат, но точку ставить было бы некуда:
 * строка счётчика не совпала бы ни с одной клеткой. У ученика поля `group` нет
 * вовсе (раздел 3 `docs/group-lessons-design.md` запрещает ему отличать
 * групповое занятие от индивидуального), и его клетка это его собственный слот,
 * то есть вторая ветка.
 *
 * ⚠️ **Урок последним, а не первым.** У занятия серии урок бывает, но ключ у
 * чата всё равно по паре: адресуя уроком, мы получили бы от счётчика адрес
 * пары и снова не нашли бы клетку. Урок остаётся адресом там, где занятия нет
 * вовсе: разовое занятие и осиротевший урок.
 *
 * ⚠️ **У ЗАГЛУШКИ перенесённого занятия чата нет** (`state: "moved_away"`).
 * Ключ чата хранит исходную дату, поэтому старое и новое место занятия это один
 * и тот же чат; показав его на обеих клетках, мы получили бы две точки на одну
 * новость и два места, где человек пишет «туда же». Занятие живёт на новом
 * месте, там чат и стоит.
 */
export function occurrenceChatAddress(entry: ScheduleEntry): string | null {
  if (entry.state === "moved_away") return null;
  if (entry.group !== undefined && entry.occursOn !== null) {
    return occurrenceChatId({ kind: "group", groupId: entry.group.id, occursOn: entry.occursOn });
  }
  if (entry.source === "series" && entry.slotId !== null && entry.occursOn !== null) {
    return occurrenceChatId({ kind: "pair", slotId: entry.slotId, occursOn: entry.occursOn });
  }
  if (entry.lessonId !== null) {
    return occurrenceChatId({ kind: "lesson", lessonId: entry.lessonId });
  }
  return null;
}

/**
 * Ключи занятий, у которых есть непрочитанное: по ним сетка ставит точку.
 *
 * ⚠️ **Сопоставление идёт по АДРЕСУ, который вернула ручка, а не по ключу
 * строки сетки.** Ключи это разные пространства имён: у строки сетки он
 * `o:<слот>:<дата>`, у чата `pair:<слот>:<дата>` либо `group:<группа>:<дата>`.
 * Пересчитывает одно в другое `occurrenceChatAddress`, и это единственное место
 * перевода.
 */
export function chatDotKeys(
  entries: readonly ScheduleEntry[],
  unreadAt: (chatId: string) => number,
): ReadonlySet<string> {
  const out = new Set<string>();
  for (const entry of entries) {
    const address = occurrenceChatAddress(entry);
    if (address !== null && unreadAt(address) > 0) out.add(entry.key);
  }
  return out;
}

/**
 * Пустая лента у строки звонка с преподавателями (9.4
 * `docs/teacher-calls-design.md`). Одна на обе стороны: пишут друг другу
 * преподаватели, ученика в звонке нет, и «написать ученику» было бы неправдой.
 * «До и после» здесь нет тоже: после конца звонка исчезает и чат.
 */
export const CALL_ENTRY_CHAT_EMPTY = "Написанное здесь читают все участники звонка";

/**
 * Пустая лента в карточке. Четыре текста из раздела 13 проекта, дословно, и
 * пятый у звонка с преподавателями (`call`).
 *
 * ⚠️ **Оборота «во время урока» нет ни в одном**, и это требование проекта:
 * тексты читают ученик и родитель, а чат существует и до занятия, и после него.
 *
 * ⚠️ Звонок спрашивается ДО роли: у гостя звонка роль `student` (права гостя
 * это права ученика), и по одной роли он прочёл бы «написать преподавателю».
 */
export function emptyEntryChatText(
  role: "teacher" | "student",
  scope: "pair" | "group",
  call = false,
): string {
  if (call) return CALL_ENTRY_CHAT_EMPTY;
  if (role === "teacher") {
    return scope === "group"
      ? "Общий чат занятия: написанное здесь читают все ученики этого занятия"
      : "Здесь можно написать ученику до занятия и после него";
  }
  return scope === "group"
    ? "Это общий чат занятия: написанное здесь читают все, кто занимается вместе с вами. Ответа сразу может не быть"
    : "Здесь можно написать преподавателю. Ответа сразу может не быть";
}

/**
 * Убирать ли даёт эта роль это сообщение.
 *
 * ⚠️ **Правило повторяет предикат сервера, а не заменяет его**: преподаватель
 * убирает любое сообщение своего занятия, ученик только своё. Здесь оно нужно
 * ровно затем, чтобы не рисовать кнопку, ведущую в отказ (кнопка, ведущая в
 * отказ, хуже отсутствующей), а решает всё равно `WHERE` в `repo`.
 */
export function canRemoveChatMessage(role: "teacher" | "student", mine: boolean): boolean {
  return role === "teacher" || mine;
}

export interface EntryChatView {
  /**
   * Рисовать ли блок чата вовсе.
   *
   * ⚠️ **Ложь означает «чата у этого занятия нет», а не «он не загрузился».**
   * Так выглядит группа с погашенным продуктовым рычагом либо со скрытым
   * составом: сервер отвечает «не найдено». Пустой блок на этот случай обещал
   * бы канал, которого нет.
   */
  shown: boolean;
  body: ChatBody;
  /** Слова пустой ленты, либо `null`, если лента не пуста. */
  emptyText: string | null;
  /** Постоянная подпись у поля, либо `null`: она есть только у группы. */
  formNote: string | null;
  /** Открыто ли окно письма. Считает сервер, здесь только протаскивается. */
  canWrite: boolean;
}

/**
 * Что показывать в карточке.
 *
 * ⚠️ **Точки на самом блоке нет и не будет.** В карточке чат уже открыт, а
 * новость про непрочитанное живёт в клетке сетки, откуда карточку и открывают:
 * точка над открытой лентой отвечала бы на вопрос, который человек уже закрыл.
 */
export function entryChatView(args: {
  role: "teacher" | "student";
  state: OccurrenceChatState;
  /** Строка звонка с преподавателями: свои слова пустой ленты. */
  call?: boolean;
}): EntryChatView {
  const { state } = args;
  if (state.absent) {
    return { shown: false, body: "loading", emptyText: null, formNote: null, canWrite: false };
  }

  const page = state.page;
  const body = chatBody({
    loaded: page !== null,
    failed: state.failed,
    empty: page !== null && page.items.length === 0,
  });

  return {
    shown: true,
    body,
    emptyText:
      page !== null && body === "empty"
        ? emptyEntryChatText(args.role, page.scope, args.call === true)
        : null,
    formNote: page !== null && page.scope === "group" ? GROUP_FORM_NOTE : null,
    canWrite: page?.canWrite ?? false,
  };
}
